(() => {
  if (customElements.get('frisson-meter')) return;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pending = new Set();
  const openTooltips = new Set();
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(({target, isIntersecting}) => {
      if (!isIntersecting) return;
      observer.unobserve(target);
      pending.delete(target);
      target.play();
    });
  }, {threshold: 0.1}) : null;

  class FrissonMeter extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.line = this.querySelector('.frisson-meter__line');
      if (!this.line) return;
      this.tooltip = this.querySelector('.frisson-meter__tooltip');
      const score = Math.round(Math.min(10, Math.max(0, Number(this.dataset.score) || 0)) * 10) / 10;
      this.dataset.score = score.toFixed(1);
      const number = this.querySelector('.frisson-meter__score');
      if (number) number.textContent = score.toFixed(1);
      this.querySelectorAll('.frisson-meter__segment-fill').forEach((fill, i) => {
        fill.style.setProperty('--frisson-fill', String(Math.round(Math.min(1, Math.max(0, score - i)) * 100)) + '%');
      });
      const descriptor = this.dataset.showDescriptor === 'true'
        ? (score >= 9.7 ? this.dataset.topDescriptor : score >= 9 ? this.dataset.eventDescriptor : '') : '';
      const caption = this.querySelector('.frisson-meter__descriptor');
      if (caption) { caption.textContent = descriptor || ''; caption.hidden = !descriptor; }
      const description = [
        (this.dataset.label || 'Frisson') + ' score ' + score.toFixed(1) + ' out of 10.',
        this.tooltip?.textContent.trim(), descriptor
      ].filter(Boolean).join(' ');
      this.line.setAttribute('aria-label', description);

      // Preserve any enclosing link's action. Inline shortcuts are noninteractive.
      if (this.tooltip && this.closest('a, button, [role="link"]')) {
        const span = document.createElement('span');
        span.className = this.line.className;
        span.setAttribute('role', 'img');
        span.setAttribute('aria-label', description);
        span.append(...this.line.childNodes);
        this.line.replaceWith(span);
        this.line = span;
      }
      if (this.tooltip) this.setupTooltip();
      if (this.dataset.animation === 'done') return;
      if (this.dataset.animate !== 'true' || motion.matches || !observer) {
        this.dataset.animation = 'done';
      } else {
        this.dataset.animation = 'pending';
        pending.add(this);
        observer.observe(this);
      }
    }
    disconnectedCallback() {
      observer?.unobserve(this);
      pending.delete(this);
      this.hideTooltip();
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.animationTimer);
      clearTimeout(this.hideTimer);
      if (this.dataset.animation === 'playing') this.dataset.animation = 'done';
    }
    play() {
      this.dataset.animation = motion.matches ? 'done' : 'playing';
      this.animationTimer = setTimeout(() => { this.dataset.animation = 'done'; }, 560);
    }
    setupTooltip() {
      const options = {signal: this.controller.signal};
      this.addEventListener('pointerenter', () => { clearTimeout(this.hideTimer); this.showTooltip(); }, options);
      this.addEventListener('pointerleave', () => {
        if (!this.pinned && !this.contains(document.activeElement)) this.hideTimer = setTimeout(() => this.hideTooltip(), 120);
      }, options);
      this.addEventListener('focusin', () => this.showTooltip(), options);
      this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) this.hideTooltip(); }, options);
      if (this.line.tagName === 'BUTTON') this.line.addEventListener('click', () => {
        this.pinned = !this.pinned;
        if (this.pinned) this.showTooltip(); else this.hideTooltip();
      }, options);
    }
    showTooltip() {
      if (!this.tooltip) return;
      this.tooltip.hidden = false;
      if (this.tooltip.showPopover && !this.tooltip.matches(':popover-open')) this.tooltip.showPopover();
      openTooltips.add(this);
      if (this.line.tagName === 'BUTTON') this.line.setAttribute('aria-expanded', 'true');
      this.positionTooltip();
    }
    positionTooltip() {
      const anchor = this.line.getBoundingClientRect();
      const box = this.tooltip.getBoundingClientRect();
      const x = Math.max(12, Math.min(anchor.left, document.documentElement.clientWidth - box.width - 12));
      const below = anchor.bottom + 8;
      const y = below + box.height <= window.innerHeight - 12 ? below : Math.max(12, anchor.top - box.height - 8);
      this.tooltip.style.left = x + 'px';
      this.tooltip.style.top = y + 'px';
    }
    hideTooltip() {
      if (!this.tooltip) return;
      if (this.tooltip.hidePopover && this.tooltip.matches(':popover-open')) this.tooltip.hidePopover();
      this.tooltip.hidden = true;
      if (this.line?.tagName === 'BUTTON') this.line.setAttribute('aria-expanded', 'false');
      this.pinned = false;
      openTooltips.delete(this);
    }
  }
  customElements.define('frisson-meter', FrissonMeter);

  const settleMotion = () => {
    if (!motion.matches) return;
    pending.forEach(meter => { observer?.unobserve(meter); meter.dataset.animation = 'done'; });
    pending.clear();
    document.querySelectorAll('.frisson-meter[data-animation="playing"]').forEach(meter => {
      clearTimeout(meter.animationTimer); meter.dataset.animation = 'done';
    });
  };
  motion.addEventListener?.('change', settleMotion);
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') openTooltips.forEach(meter => meter.hideTooltip());
  });
  document.addEventListener('pointerdown', event => {
    openTooltips.forEach(meter => { if (!meter.contains(event.target)) meter.hideTooltip(); });
  });
  window.addEventListener('resize', () => openTooltips.forEach(meter => meter.positionTooltip()), {passive: true});
  window.addEventListener('scroll', () => openTooltips.forEach(meter => meter.positionTooltip()), {passive: true, capture: true});

  // The template is rendered by the same Liquid snippet as sections and blocks.
  // Replace text nodes only. Never rewrite HTML, attributes, links, scripts or inputs.
  const excluded = 'frisson-meter, script, style, template, noscript, textarea, input, select, option, pre, code, svg, math, [contenteditable="true"], [data-frisson-ignore]';
  const token = /\[\[frisson:\s*([+-]?\d+(?:\.\d+)?)\s*\]\]/gi;
  function replaceShortcuts(root) {
    const template = document.getElementById('FrissonInlineTemplate');
    if (!template || !root?.isConnected || root.parentElement?.closest(excluded)) return;
    if (root.nodeType === 1 && root.matches(excluded)) return;
    const nodes = [];
    if (root.nodeType === 3) nodes.push(root);
    else if (root.nodeType === 1) {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: node => node.parentElement?.closest(excluded) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
      });
      while (walker.nextNode()) nodes.push(walker.currentNode);
    }
    nodes.forEach(node => {
      const text = node.nodeValue;
      token.lastIndex = 0;
      if (!token.test(text)) return;
      token.lastIndex = 0;
      const fragment = document.createDocumentFragment();
      let start = 0;
      for (const match of text.matchAll(token)) {
        fragment.append(document.createTextNode(text.slice(start, match.index)));
        const meter = template.content.firstElementChild.cloneNode(true);
        meter.dataset.score = String(Number(match[1]));
        fragment.append(meter);
        start = match.index + match[0].length;
      }
      fragment.append(document.createTextNode(text.slice(start)));
      node.replaceWith(fragment);
    });
  }
  function boot() {
    replaceShortcuts(document.body);
    const changes = new MutationObserver(records => {
      const roots = new Set();
      records.forEach(record => {
        if (record.type === 'characterData') roots.add(record.target);
        else record.addedNodes.forEach(node => roots.add(node));
      });
      roots.forEach(replaceShortcuts);
    });
    changes.observe(document.body, {childList: true, characterData: true, subtree: true});
  }
  document.addEventListener('shopify:section:load', event => replaceShortcuts(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once: true}); else boot();
})();
